import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import Timetable from "@/pages/Timetable";
import Homework from "@/pages/Homework";
import Goals from "@/pages/Goals";
import Revisions from "@/pages/Revisions";
import Flashcards from "@/pages/Flashcards";
import Exercises from "@/pages/Exercises";
import Progress from "@/pages/Progress";
import Parent from "@/pages/Parent";
import Pricing from "@/pages/Pricing";
import Profile from "@/pages/Profile";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="app-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-indigo-600 animate-pulse" />
          <p className="text-sm text-slate-500 font-medium">Chargement de ProEdu…</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AuthCallback() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/google-session", { session_id: sessionId });
        if (data.token) localStorage.setItem("studykit_token", data.token);
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        toast.success("Bienvenue sur ProEdu !");
        navigate("/dashboard", { replace: true });
      } catch {
        toast.error("La connexion Google a échoué. Réessaie.");
        navigate("/auth", { replace: true });
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-indigo-600 animate-pulse" />
        <p className="text-sm text-slate-500 font-medium">Connexion avec Google…</p>
      </div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route element={<Protected><Layout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/timetable" element={<Timetable />} />
        <Route path="/homework" element={<Homework />} />
        <Route path="/goals" element={<Goals />} />
        <Route path="/revisions" element={<Revisions />} />
        <Route path="/flashcards" element={<Flashcards />} />
        <Route path="/exercises" element={<Exercises />} />
        <Route path="/progress" element={<Progress />} />
        <Route path="/parent" element={<Parent />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/profile" element={<Profile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    if (localStorage.getItem("studykit_theme") === "dark") {
      document.documentElement.classList.add("dark");
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
